import { useState } from "react";
import { ArrowDown, ArrowUp, Camera, Check, ExternalLink, ImagePlus, MoreVertical, Scissors, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api, apiError } from "@/lib/api";
import { Panel } from "../ui";
import { VideoManager } from "./VideoManager";

const PhotoCard = ({ ph, index, run, busy }) => (
  <div data-testid={`photo-${index}`} className="relative border border-[#D5B98C]/60 bg-white">
    <div className="relative aspect-[4/5] bg-[#E9DFCE]">
      <img src={ph.url} alt={ph.alt} className="w-full h-full object-contain" />
      {index === 0 && <span className="absolute top-1.5 left-1.5 text-[9px] uppercase tracking-widest bg-[#39251D] text-[#F5F0E8] px-1.5 py-0.5">Principale</span>}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid={`photo-menu-${index}`} disabled={busy} className="absolute top-1.5 right-1.5 w-8 h-8 bg-[#F5F0E8]/90 flex items-center justify-center"><MoreVertical className="w-4 h-4" /></button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-[#F5F0E8] w-56">
          <DropdownMenuItem data-testid={`photo-first-${index}`} onClick={() => run("move", ph, { direction: "first" })}><Star className="w-4 h-4 mr-2" />Imposta principale</DropdownMenuItem>
          <DropdownMenuItem data-testid={`photo-up-${index}`} onClick={() => run("move", ph, { direction: "up" })}><ArrowUp className="w-4 h-4 mr-2" />Sposta prima</DropdownMenuItem>
          <DropdownMenuItem data-testid={`photo-down-${index}`} onClick={() => run("move", ph, { direction: "down" })}><ArrowDown className="w-4 h-4 mr-2" />Sposta dopo</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid={`photo-crop45-${index}`} onClick={() => run("crop", ph, { operation: "crop_4_5" })}><Scissors className="w-4 h-4 mr-2" />Ritaglia 4:5</DropdownMenuItem>
          <DropdownMenuItem data-testid={`photo-crop11-${index}`} onClick={() => run("crop", ph, { operation: "crop_square" })}><Scissors className="w-4 h-4 mr-2" />Ritaglia quadrato</DropdownMenuItem>
          {ph.original_url && <DropdownMenuItem asChild><a href={ph.original_url} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4 mr-2" />Apri originale</a></DropdownMenuItem>}
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid={`photo-delete-${index}`} onClick={() => run("delete", ph)} className="text-[#8a2f22]"><Trash2 className="w-4 h-4 mr-2" />Elimina foto</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
    <div className="p-2">
      <button data-testid={`photo-approve-${index}`} onClick={() => run("approve", ph, { approved: !ph.approved })} disabled={busy}
        className={`w-full text-[10px] uppercase tracking-widest py-1.5 inline-flex items-center justify-center gap-1 ${ph.approved ? "bg-[#3d5a3a]/10 text-[#3d5a3a]" : "bg-[#39251D] text-[#F5F0E8]"}`}>
        {ph.approved ? <><Check className="w-3 h-3" />Visibile</> : "Rendi visibile"}</button>
    </div>
  </div>
);

export const PhotoManager = ({ product, onChange }) => {
  const [busy, setBusy] = useState(false);
  const photos = product.photos || [];

  const run = async (kind, ph, body) => {
    setBusy(true);
    try {
      const base = `/admin/products/${product.id}/photos/${ph.id}`;
      if (kind === "crop") { onChange((await api.post(`${base}/edit`, body)).data); toast.success("Ritaglio creato"); }
      else if (kind === "move") onChange((await api.post(`${base}/move`, body)).data);
      else if (kind === "approve") onChange((await api.patch(base, body)).data);
      else if (kind === "delete") { if (!window.confirm("Eliminare la foto dalla scheda? L'originale resta archiviato.")) return; onChange((await api.delete(base)).data); }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const upload = async (files) => {
    if (!files.length) return;
    setBusy(true);
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    try { onChange((await api.post(`/admin/products/${product.id}/photos`, fd)).data); toast.success("Foto caricate"); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Panel title="Fotografie" testId="photo-manager" actions={
      <div className="flex gap-4">
        <label data-testid="photo-gallery-button" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#956842] cursor-pointer">
          <ImagePlus className="w-4 h-4" /> Galleria
          <input data-testid="photo-upload-input" type="file" multiple accept="image/*" className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
        </label>
        <label data-testid="photo-camera-button" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#956842] cursor-pointer">
          <Camera className="w-4 h-4" /> Scatta
          <input data-testid="photo-camera-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
        </label>
      </div>
    }>
      {photos.length === 0 && <p className="text-sm text-[#242321]/60">{product.images?.length ? "Prodotto con immagini esterne." : "Nessuna foto caricata."}</p>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {photos.map((ph, i) => <PhotoCard key={ph.id} ph={ph} index={i} run={run} busy={busy} />)}
      </div>
      <p className="mt-4 text-[11px] text-[#242321]/55">Le foto vengono pubblicate così come le carichi (solo ottimizzate per il web). La prima è la foto principale.</p>
      <VideoManager product={product} onChange={onChange} />
    </Panel>
  );
};
